const MAX_STRENGTHS = 3;
// The advanced framework returns up to 7 priority improvements (PI-1 to PI-7)
const MAX_PRIORITY_IMPROVEMENTS = 7;

function toNumber(value) {
  if (value === null || value === undefined) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function criteriaSortKey(value) {
  if (value === null || value === undefined) return [2, ""];
  if (typeof value === "number") return [0, value];

  const text = String(value).trim();
  const numeric = Number(text);
  if (Number.isFinite(numeric)) return [0, numeric];

  const segments = text.split(/[^0-9]+/).filter(Boolean);
  if (segments.length > 0) {
    const numbers = segments.map(Number);
    if (numbers.every((n) => Number.isFinite(n))) {
      return [1, ...numbers];
    }
  }

  return [2, text.toLowerCase()];
}

function alphabeticalKey(value) {
  return String(value ?? "").trim().toLowerCase();
}

function compareKeys(a, b) {
  const length = Math.max(a.length, b.length);

  for (let index = 0; index < length; index += 1) {
    const left = a[index];
    const right = b[index];

    if (left === undefined && right === undefined) return 0;
    if (left === undefined) return -1;
    if (right === undefined) return 1;
    if (left < right) return -1;
    if (left > right) return 1;
  }

  return 0;
}

function strengthSortMeta(item) {
  const score =
    toNumber(item?.score_percentage) ??
    toNumber(item?.score) ??
    null;

  const criteriaNumber =
    item?.criteria_number ??
    item?.criteriaNumber ??
    null;

  const descriptor =
    item?.criteria ??
    item?.title ??
    item?.text ??
    (typeof item === "string" ? item : "");

  return {
    score,
    criteriaKey: criteriaSortKey(criteriaNumber),
    descriptorKey: alphabeticalKey(descriptor),
  };
}

function strengthsComparator(aEntry, bEntry) {
  const left = strengthSortMeta(aEntry.item);
  const right = strengthSortMeta(bEntry.item);

  if (left.score !== null || right.score !== null) {
    if (left.score === null) return 1;
    if (right.score === null) return -1;
    if (left.score !== right.score) return right.score - left.score;
  }

  const criteriaComparison = compareKeys(left.criteriaKey, right.criteriaKey);
  if (criteriaComparison !== 0) return criteriaComparison;

  const descriptorComparison = left.descriptorKey.localeCompare(right.descriptorKey);
  if (descriptorComparison !== 0) return descriptorComparison;

  return aEntry.index - bEntry.index;
}

function improvementSortMeta(item) {
  const score =
    toNumber(item?.score_percentage) ??
    toNumber(item?.score) ??
    null;

  const criteriaNumber =
    item?.criteria_number ??
    item?.criteriaNumber ??
    null;

  const descriptor =
    item?.area ??
    item?.criteria ??
    item?.title ??
    (item && typeof item === "object" ? JSON.stringify(item) : String(item ?? ""));

  return {
    score,
    criteriaKey: criteriaSortKey(criteriaNumber),
    descriptorKey: alphabeticalKey(descriptor),
  };
}

function improvementsComparator(aEntry, bEntry) {
  const left = improvementSortMeta(aEntry.item);
  const right = improvementSortMeta(bEntry.item);

  if (left.score !== null || right.score !== null) {
    if (left.score === null) return 1;
    if (right.score === null) return -1;
    if (left.score !== right.score) return left.score - right.score;
  }

  const criteriaComparison = compareKeys(left.criteriaKey, right.criteriaKey);
  if (criteriaComparison !== 0) return criteriaComparison;

  const descriptorComparison = left.descriptorKey.localeCompare(right.descriptorKey);
  if (descriptorComparison !== 0) return descriptorComparison;

  return aEntry.index - bEntry.index;
}

function normalizeStrengths(strengths) {
  if (!Array.isArray(strengths)) return [];
  return Array.from(strengths)
    .map((item, index) => ({ item, index }))
    .sort(strengthsComparator)
    .map((entry) => entry.item)
    .slice(0, MAX_STRENGTHS);
}

function normalizeImprovements(improvements) {
  if (!Array.isArray(improvements)) return [];
  return Array.from(improvements)
    .map((item, index) => ({ item, index }))
    .sort(improvementsComparator)
    .map((entry) => entry.item)
    .slice(0, MAX_PRIORITY_IMPROVEMENTS);
}

export function normalizeAnalysis(analysis) {
  if (!analysis || typeof analysis !== "object") return analysis;

  const normalized = { ...analysis };

  normalized.key_strengths = normalizeStrengths(analysis.key_strengths);
  normalized.priority_improvements = normalizeImprovements(
    analysis.priority_improvements
  );

  return normalized;
}

export const NORMALISED_STRENGTH_LIMIT = MAX_STRENGTHS;
export const NORMALISED_IMPROVEMENT_LIMIT = MAX_PRIORITY_IMPROVEMENTS;
